import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, Tag } from 'lucide-react';
import { useStoreInfo } from '@/lib/store';
import { formatPrice, formatPriceRange, truncateText, getDomain, hasDiscount, getDiscountBadgeText, resolveImageUrl } from '@/lib/utils';
import { Product } from '@/types';
import { NotificationPopup } from '@/components/notifications/notification-prompt';
import { Button } from '@/components/ui/button';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

interface CategoryWithImage {
  name: string;
  image: string;
}

export function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<CategoryWithImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const { storeInfo } = useStoreInfo();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const domain = getDomain();
        const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/products.php?domain=${domain}`);
        const data = await response.json();
        
        if (data.status) {
          const products = data.products.filter((p: Product) => p.status === 1);
          setFeaturedProducts(products.slice(0, 12));
          
          const categoriesMap = new Map<string, string>();
          products.forEach((product: Product) => {
            if (product.category && !categoriesMap.has(product.category) && product.images?.length > 0) {
              categoriesMap.set(product.category, product.images[0]);
            }
          });

          const categoriesWithImages = Array.from(categoriesMap).map(([name, image]) => ({
            name,
            image
          }));

          setCategories(categoriesWithImages);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate('/products', { state: { searchTerm: searchTerm.trim() } });
    }
  };

  const handleCategoryClick = (category: string) => {
    navigate('/products', { state: { selectedCategory: category } });
  };

  return (
    <>
      <Helmet>
        <title>{storeInfo?.site_name || 'Food Ordering'}</title>
        <meta name="description" content="Order delicious food online" />
      </Helmet>

      <NotificationPopup />

      {/* Hero Section with Slider - Optimized for All Screens */}
      <div className="relative bg-brand-primary w-full">
        <Swiper
          modules={[Pagination, Autoplay]}
          pagination={{ clickable: true }}
          autoplay={{ delay: 5000 }}
          className="h-[180px] xs:h-[200px] sm:h-[280px] md:h-[350px] lg:h-[450px] xl:h-[550px] w-full"
        >
          {storeInfo?.heroSlider?.length ? (
            storeInfo.heroSlider.map((slide, index) => (
              <SwiperSlide key={index}>
                <Link 
                  to="/categories"
                  className="block relative h-full w-full cursor-pointer"
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img
                      src={resolveImageUrl(slide)}
                      alt={`Slide ${index + 1}`}
                      className="w-full h-full object-contain md:object-cover"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/30 hover:bg-black/40 transition-colors" />
                </Link>
              </SwiperSlide>
            ))
          ) : (
            <SwiperSlide>
              <div className="h-full w-full bg-gradient-to-r from-brand-dark to-brand-primary" />
            </SwiperSlide>
          )}
        </Swiper>
      </div>

      {/* Search Section */}
      <div className="bg-white dark:bg-gray-800 shadow-lg">
        <div className="max-w-xl mx-auto px-4 py-6">
          <form onSubmit={handleSearch} className="relative">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-full border-none ring-2 ring-gray-200 dark:ring-gray-700 focus:ring-brand-primary dark:focus:ring-brand-light text-lg dark:bg-gray-700 dark:text-white"
            />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
          </form>
        </div>
      </div>

      {/* Categories Slider */}
      <div className="py-12 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold font-display text-gray-900 dark:text-white mb-6">
            Categories
          </h2>
          
          <Swiper
            modules={[Pagination]}
            pagination={{ clickable: true }}
            slidesPerView={2}
            spaceBetween={12}
            breakpoints={{
              480: { slidesPerView: 3, spaceBetween: 16 },
              640: { slidesPerView: 4, spaceBetween: 16 },
              1024: { slidesPerView: 5, spaceBetween: 20 },
            }}
            className="category-slider"
          >
            {categories.map((category, index) => (
              <SwiperSlide key={index}>
                <button
                  onClick={() => handleCategoryClick(category.name)}
                  className="relative w-full aspect-square overflow-hidden rounded-lg group cursor-pointer"
                >
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center p-2">
                    <h3 className="text-sm sm:text-base font-medium text-white text-center">
                      {category.name}
                    </h3>
                  </div>
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      {/* Featured Products */}
      <div className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold font-display text-gray-900 dark:text-white">Featured Products</h2>
            <Link to="/products">
              <Button variant="outline">View All Products</Button>
            </Link>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {loading ? (
              [...Array(12)].map((_, i) => (
                <div key={i} className="animate-pulse food-card">
                  <div className="aspect-square bg-gray-200 dark:bg-gray-700" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                  </div>
                </div>
              ))
            ) : (
              featuredProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="food-card group"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={product.images[0]}
                      alt={product.produt_name}
                      className="food-image group-hover:scale-110 transition-transform duration-300"
                    />
                    {product.postType === "1" && (
                      <div className="absolute top-2 right-2 bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                        Pre-Order
                      </div>
                    )}
                    {hasDiscount(product) && (
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                        <Tag className="h-3 w-3" />
                        {getDiscountBadgeText(product)}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-lg mb-2 text-gray-900 dark:text-white group-hover:text-brand-primary transition-colors line-clamp-2">
                      {truncateText(product.produt_name, 50)}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      {product.category || 'Uncategorized'}
                    </p>
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-brand-primary dark:text-brand-light">
                        {product.variations[0].variation !== 'Nil' 
                          ? formatPriceRange(product.variations, storeInfo?.currenCy)
                          : formatPrice(parseFloat(String(product.sellingprice).replace(/[^0-9.-]+/g, '')) || 0, storeInfo?.currenCy)}
                      </span>
                      {hasDiscount(product) && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Buy {product.discount_qty}+ for discount
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}
